import { BUTLER_TAB, isButlerTab, type ButlerTabType } from '@cart-services/constants/butler-tab'
import { ProjectCode } from '@constants'
import { FAST_ORDER_ITEM_CHECK_STATUS, type FastOrderItemCheckStatusType } from '@hooks-api/types/dataModel/fast-order'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export const STM_QUICK_ORDER_STORAGE_KEY = 'stmQuickOrderStorage'
export const DEFAULT_QUICK_ORDER_PROJECT_CODE = ProjectCode.DCS

export interface CsvProductSnapshot {
  sku: string
  variationId: number
  parentProductId: number
  parentProductName: string
  displayName: string
  specName: string | null
  imageUrl: string | null
  unit: string
  unitPrice: number
}

export interface CsvItemCheck {
  status: FastOrderItemCheckStatusType
  remainingQuantity: number | null
  unitPrice: number | null
  amount: number | null
  errorMessage: string | null
}

export interface CsvButlerItem {
  key: string
  projectCode: ProjectCode
  realSku: string
  quantity: number
  sourceRowNumbers: number[]
  product: CsvProductSnapshot
  checked: boolean
  isDeleted: boolean
  /** null 代表尚未檢查或重新檢查中 */
  check: CsvItemCheck | null
}

export interface CsvResult {
  fileName: string
  items: CsvButlerItem[]
}

/** 後端已合併同品牌同 SKU，兩者組合即唯一 */
export const buildCsvItemKey = (projectCode: ProjectCode, realSku: string) => `${projectCode}:${realSku}`

export const MIN_CSV_ITEM_QUANTITY = 1

export const isCsvItemQuantityInvalid = (item: CsvButlerItem) => item.quantity < MIN_CSV_ITEM_QUANTITY

// 數量不合法的商品不送檢查，不能算在等檢查結果，否則整頁會卡在「重新檢查中」
export const isCsvItemPending = (item: CsvButlerItem) => item.check === null && !isCsvItemQuantityInvalid(item)

export const isCsvItemBlocked = (item: CsvButlerItem) => item.check !== null && item.check.status !== FAST_ORDER_ITEM_CHECK_STATUS.PASSED

const withBlockedGuard = (item: CsvButlerItem): CsvButlerItem =>
  (isCsvItemBlocked(item) && item.checked ? { ...item, checked: false } : item)

interface StmQuickOrderState {
  selectedTab: ButlerTabType
  selectedProjectCode: ProjectCode
  csv: CsvResult & {
    brandFilter: ProjectCode | null
  }
  setSelectedTab: (selectedTab: ButlerTabType) => void
  setSelectedProjectCode: (selectedProjectCode: ProjectCode) => void
  resetSelectedProjectCode: () => void
  setCsvResult: (result: CsvResult) => void
  upsertCsvItem: (key: CsvButlerItem['key'], patch: Partial<Omit<CsvButlerItem, 'key'>>) => void
  setCsvItemChecks: (checks: Record<CsvButlerItem['key'], CsvItemCheck | null>) => void
  setCsvBrandFilter: (brandFilter: ProjectCode | null) => void
  resetCsv: () => void
  reset: () => void
}

type StmQuickOrderValues = Pick<StmQuickOrderState, 'selectedTab' | 'selectedProjectCode' | 'csv'>

const defaultCsv: StmQuickOrderState['csv'] = {
  fileName: '',
  items: [],
  brandFilter: null,
}

const defaultState = {
  selectedTab: BUTLER_TAB.CSV,
  selectedProjectCode: DEFAULT_QUICK_ORDER_PROJECT_CODE,
  csv: defaultCsv,
} satisfies StmQuickOrderValues

export const useStmQuickOrderStore = create<StmQuickOrderState>()(
  persist(
    set => ({
      ...defaultState,
      setSelectedTab: selectedTab => set({ selectedTab }),
      setSelectedProjectCode: selectedProjectCode => set({ selectedProjectCode }),
      resetSelectedProjectCode: () => set({ selectedProjectCode: DEFAULT_QUICK_ORDER_PROJECT_CODE }),
      setCsvResult: ({ fileName, items }) => set({ csv: { fileName, items: items.map(withBlockedGuard), brandFilter: null } }),
      upsertCsvItem: (key, patch) => set(state => ({
        csv: {
          ...state.csv,
          items: state.csv.items.map((item) => {
            if (item.key !== key) return item
            // 檢查結果綁定送檢時的數量，數量一變就作廢，等重查寫回
            const quantityChanged = patch.quantity !== undefined && patch.quantity !== item.quantity
            return withBlockedGuard({ ...item, ...patch, ...(quantityChanged && { check: null }) })
          }),
        },
      })),
      setCsvItemChecks: checks => set(state => ({
        csv: {
          ...state.csv,
          items: state.csv.items.map(item => (item.key in checks ? withBlockedGuard({ ...item, check: checks[item.key] ?? null }) : item)),
        },
      })),
      setCsvBrandFilter: brandFilter => set(state => ({ csv: { ...state.csv, brandFilter } })),
      resetCsv: () => set({ csv: defaultCsv }),
      reset: () => set(defaultState),
    }),
    {
      name: STM_QUICK_ORDER_STORAGE_KEY,
      version: 2,
      storage: createJSONStorage(() => localStorage),
      // 庫存與計價是即時值，重新載入後由確認頁重查，不沿用舊結果
      partialize: state => ({
        selectedTab: state.selectedTab,
        selectedProjectCode: state.selectedProjectCode,
        csv: { ...state.csv, items: state.csv.items.map(item => ({ ...item, check: null })) },
      }),
      migrate: (persistedState, version) => {
        let state = persistedState as StmQuickOrderValues
        if (version < 1) {
          state = { ...state, csv: defaultCsv }
        }
        if (version < 2 && !isButlerTab(state.selectedTab)) {
          state = { ...state, selectedTab: defaultState.selectedTab }
        }
        return state
      },
    },
  ),
)
